import { Contract, Coin, TxnStdFee } from "./contract";
import { SecretNetworkClient } from "secretjs";

export type Addr = string;
export interface ConfigResponse {
  fee_address?: Addr | null;
  generator_address?: Addr | null;
  owner: Addr;
  pair_configs: PairConfig[];
  token_code_id: number;
  whitelist_code_id: number;
  [k: string]: unknown;
}
export interface PairConfig {
  code_hash: string;
  code_id: number;
  is_disabled: boolean;
  is_generator_disabled: boolean;
  maker_fee_bps: number;
  pair_type: string;
  total_fee_bps: number;
  [k: string]: unknown;
}
export type ExecuteMsg = {
  post_creation: {
    pair_contract: Addr;
    pair_contract_hash: string;
    [k: string]: unknown;
  };
} | {
  update_config: {
    fee_address?: string | null;
    generator_address?: string | null;
    token_code_hash?: string | null;
    token_code_id?: number | null;
    whitelist_code_id?: number | null;
    [k: string]: unknown;
  };
} | {
  update_pair_config: {
    config: PairConfig;
    [k: string]: unknown;
  };
} | {
  create_pair: {
    asset_infos: AssetInfo[];
    init_params?: Binary | null;
    pair_type: string;
    prng_seed: Binary;
    viewing_key: string;
    [k: string]: unknown;
  };
} | {
  deregister: {
    asset_infos: AssetInfo[];
    [k: string]: unknown;
  };
} | {
  propose_new_owner: {
    expires_in: number;
    owner: string;
    [k: string]: unknown;
  };
} | {
  drop_ownership_proposal: {
    [k: string]: unknown;
  };
} | {
  claim_ownership: {
    [k: string]: unknown;
  };
} | {
  mark_as_migrated: {
    pairs: string[];
    [k: string]: unknown;
  };
};
export type Binary = string;
export interface AssetInfo {
  contract_addr?: Addr | null;
  contract_code_hash?: string | null;
  denom?: string | null;
  [k: string]: unknown;
}
export interface InstantiateMsg {
  fee_address?: string | null;
  generator_address?: string | null;
  owner: string;
  pair_configs: PairConfig[];
  token_code_hash: string;
  token_code_id: number;
  whitelist_code_id: number;
  [k: string]: unknown;
}
export interface MigrateMsg {
  params: Binary;
  [k: string]: unknown;
}
export interface PairInfo {
  asset_infos: AssetInfo[];
  contract_addr: Addr;
  contract_code_hash: string;
  liquidity_token: Addr;
  liquidity_token_hash: string;
  pair_type: string;
  [k: string]: unknown;
}
export interface PairsResponse {
  pairs: PairInfo[];
  [k: string]: unknown;
}
export type QueryMsg = {
  config: {
    [k: string]: unknown;
  };
} | {
  pair: {
    asset_infos: AssetInfo[];
    [k: string]: unknown;
  };
} | {
  pairs: {
    limit?: number | null;
    start_after_1?: AssetInfo | null;
    start_after_2?: AssetInfo | null;
    [k: string]: unknown;
  };
} | {
  fee_info: {
    pair_type: string;
    [k: string]: unknown;
  };
} | {
  blacklisted_pair_types: {
    [k: string]: unknown;
  };
} | {
  pairs_to_migrate: {
    [k: string]: unknown;
  };
};
export interface FactoryReadOnlyInterface {
  config: () => Promise<any>;
  pair: ({
    assetInfos
  }: {
    assetInfos: AssetInfo[];
  }) => Promise<any>;
  pairs: ({
    limit,
    startAfter1,
    startAfter2
  }: {
    limit?: number;
    startAfter1?: AssetInfo;
    startAfter2?: AssetInfo;
  }) => Promise<any>;
  feeInfo: ({
    pairType
  }: {
    pairType: string;
  }) => Promise<any>;
  blacklistedPairTypes: () => Promise<any>;
  pairsToMigrate: () => Promise<any>;
}
export class FactoryQueryContract extends Contract implements FactoryReadOnlyInterface {
  constructor(client: SecretNetworkClient, contractAddress: string, contractHash?: string) {
    super(client, contractAddress, contractHash);
    this.config = this.config.bind(this);
    this.pair = this.pair.bind(this);
    this.pairs = this.pairs.bind(this);
    this.feeInfo = this.feeInfo.bind(this);
    this.blacklistedPairTypes = this.blacklistedPairTypes.bind(this);
    this.pairsToMigrate = this.pairsToMigrate.bind(this);
  }

  config = async (): Promise<any> => {
    return this.queryMsg({
      config: {}
    });
  };
  pair = async ({
    assetInfos
  }: {
    assetInfos: AssetInfo[];
  }): Promise<any> => {
    return this.queryMsg({
      pair: {
        asset_infos: assetInfos
      }
    });
  };
  pairs = async ({
    limit,
    startAfter1,
    startAfter2
  }: {
    limit?: number;
    startAfter1?: AssetInfo;
    startAfter2?: AssetInfo;
  }): Promise<any> => {
    return this.queryMsg({
      pairs: {
        limit,
        start_after_1: startAfter1,
        start_after_2: startAfter2
      }
    });
  };
  feeInfo = async ({
    pairType
  }: {
    pairType: string;
  }): Promise<any> => {
    return this.queryMsg({
      fee_info: {
        pair_type: pairType
      }
    });
  };
  blacklistedPairTypes = async (): Promise<any> => {
    return this.queryMsg({
      blacklisted_pair_types: {}
    });
  };
  pairsToMigrate = async (): Promise<any> => {
    return this.queryMsg({
      pairs_to_migrate: {}
    });
  };
}
export interface FactoryInterface extends FactoryReadOnlyInterface {
  postCreation: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    pairContract,
    pairContractHash
  }: {
    pairContract: string;
    pairContractHash: string;
  }) => Promise<any>;
  updateConfig: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    feeAddress,
    generatorAddress,
    tokenCodeHash,
    tokenCodeId,
    whitelistCodeId
  }: {
    feeAddress?: string;
    generatorAddress?: string;
    tokenCodeHash?: string;
    tokenCodeId?: number;
    whitelistCodeId?: number;
  }) => Promise<any>;
  updatePairConfig: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    config
  }: {
    config: PairConfig;
  }) => Promise<any>;
  createPair: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    assetInfos,
    initParams,
    pairType,
    prngSeed,
    viewingKey
  }: {
    assetInfos: AssetInfo[];
    initParams?: Binary;
    pairType: string;
    prngSeed: string;
    viewingKey: string;
  }) => Promise<any>;
  deregister: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    assetInfos
  }: {
    assetInfos: AssetInfo[];
  }) => Promise<any>;
  proposeNewOwner: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    expiresIn,
    owner
  }: {
    expiresIn: number;
    owner: string;
  }) => Promise<any>;
  dropOwnershipProposal: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }) => Promise<any>;
  claimOwnership: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }) => Promise<any>;
  markAsMigrated: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    pairs
  }: {
    pairs: string[];
  }) => Promise<any>;
}
export class FactoryContract extends FactoryQueryContract implements FactoryInterface {
  constructor(client: SecretNetworkClient, contractAddress: string, contractHash?: string) {
    super(client, contractAddress, contractHash);
    this.postCreation = this.postCreation.bind(this);
    this.updateConfig = this.updateConfig.bind(this);
    this.updatePairConfig = this.updatePairConfig.bind(this);
    this.createPair = this.createPair.bind(this);
    this.deregister = this.deregister.bind(this);
    this.proposeNewOwner = this.proposeNewOwner.bind(this);
    this.dropOwnershipProposal = this.dropOwnershipProposal.bind(this);
    this.claimOwnership = this.claimOwnership.bind(this);
    this.markAsMigrated = this.markAsMigrated.bind(this);
  }

  postCreation = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    pairContract,
    pairContractHash
  }: {
    pairContract: string;
    pairContractHash: string;
  }): Promise<any> => {
    return await this.executeMsg({
      post_creation: {
        pair_contract: pairContract,
        pair_contract_hash: pairContractHash
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  updateConfig = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    feeAddress,
    generatorAddress,
    tokenCodeHash,
    tokenCodeId,
    whitelistCodeId
  }: {
    feeAddress?: string;
    generatorAddress?: string;
    tokenCodeHash?: string;
    tokenCodeId?: number;
    whitelistCodeId?: number;
  }): Promise<any> => {
    return await this.executeMsg({
      update_config: {
        fee_address: feeAddress,
        generator_address: generatorAddress,
        token_code_hash: tokenCodeHash,
        token_code_id: tokenCodeId,
        whitelist_code_id: whitelistCodeId
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  updatePairConfig = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    config
  }: {
    config: PairConfig;
  }): Promise<any> => {
    return await this.executeMsg({
      update_pair_config: {
        config
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  createPair = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    assetInfos,
    initParams,
    pairType,
    prngSeed,
    viewingKey
  }: {
    assetInfos: AssetInfo[];
    initParams?: Binary;
    pairType: string;
    prngSeed: string;
    viewingKey: string;
  }): Promise<any> => {
    return await this.executeMsg({
      create_pair: {
        asset_infos: assetInfos,
        init_params: initParams,
        pair_type: pairType,
        prng_seed: prngSeed,
        viewing_key: viewingKey
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  deregister = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    assetInfos
  }: {
    assetInfos: AssetInfo[];
  }): Promise<any> => {
    return await this.executeMsg({
      deregister: {
        asset_infos: assetInfos
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  proposeNewOwner = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    expiresIn,
    owner
  }: {
    expiresIn: number;
    owner: string;
  }): Promise<any> => {
    return await this.executeMsg({
      propose_new_owner: {
        expires_in: expiresIn,
        owner
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  dropOwnershipProposal = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }): Promise<any> => {
    return await this.executeMsg({
      drop_ownership_proposal: {}
    }, userAddress, customFees, memo, transferAmount);
  };
  claimOwnership = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }): Promise<any> => {
    return await this.executeMsg({
      claim_ownership: {}
    }, userAddress, customFees, memo, transferAmount);
  };
  markAsMigrated = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    pairs
  }: {
    pairs: string[];
  }): Promise<any> => {
    return await this.executeMsg({
      mark_as_migrated: {
        pairs
      }
    }, userAddress, customFees, memo, transferAmount);
  };
}